import {
  Paper,
  Autocomplete,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import deepEqual from 'fast-deep-equal'
import { isEmpty } from 'lodash'
import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useFormContext } from 'react-hook-form'
import { VariableSizeList, ListChildComponentProps } from 'react-window'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import {
  flattenKubernetesTargets,
  unhealthyPod
} from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import Icon from 'components/Icon'
import { KubernetesListItemTags } from 'components/services/create/target-selection/kubernetes/KubernetesListItemTags'
import { KubernetesTagLabelSection } from 'components/services/create/target-selection/kubernetes/KubernetesTagLabelSection'
import {
  getKubernetesTargetingStrategy,
  KUBERNETES_TARGETING_SECTION_DISPLAY_NAMES,
  KUBERNETES_TARGETING_SECTION_NAMES
} from 'components/services/create/target-selection/service-target-utils'
import { TagTargetingAutocompleteGroupItem } from 'components/services/create/target-selection/TagTargetingAutocompleteGroupItem'
import { useFormattedK8sTargeting } from 'hooks/use-formatted-k8s-targeting'
import { useHeightMeasurement } from 'hooks/use-height-measurement'
import theme from 'theme'
import { K8sClient } from 'types/api/kubernetes/kubernetes'
import { K8sAutocompleteSearchOption, ServiceFormValues } from 'types/forms'

/**
 * This custom hook is here only to encapsulate the complex logic required to safely memoize the selectedOptionsList
 * It is NOT intended for reuse anywhere
 *
 * Simple memoization (useMemo) does not work here because referential identity changes on optionList (and possibly selectedObjects)
 *
 * Instead, this caches the previous input values (optionList, selectedObjects) and uses deep equality comparisons
 * to react to changes and only updates when absolutely necessary
 *
 * @param optionList
 * @param selectedObjects
 */
function useDeriveSelectedOptionsList(optionList, selectedObjects) {
  const [selectedOptionsList, setSelectedOptionsList] = useState([])
  // cache these for comparison
  const prevOptionList = useRef(optionList)
  const prevSelectedObjects = useRef(selectedObjects)
  useEffect(() => {
    // use deep equality comparison so we only update when necessary
    if (
      !deepEqual(optionList, prevOptionList.current) ||
      // selectedObjects is a Set, which deepEqual does not work properly on (despite it saying it supports them)
      // so we're converting them to arrays so that the comparison works
      !deepEqual(
        Array.from(selectedObjects),
        Array.from(prevSelectedObjects.current)
      )
    ) {
      setSelectedOptionsList(
        optionList.filter((option) => selectedObjects.has(option.uid))
      )
      // update cached values
      prevOptionList.current = optionList
      prevSelectedObjects.current = selectedObjects
    }
  }, [optionList, selectedObjects])

  return selectedOptionsList
}

// This component has to be able to potentially render thousands of elements
// as some customers may have several thousand k8s objects
// as such, the autocomplete dropdown list has to be virtualized using react-window
// so that it can render all of these items without locking up the browser
//
// This autocomplete virtualization implementation is based on this example from the MUI docs:
// https://mui.com/material-ui/react-autocomplete/#virtualization

/**
 * Render selectable items in the autocomplete dropdown
 */
const KubernetesAutocompleteSearchOption = React.forwardRef<
  HTMLLIElement,
  {
    option: K8sAutocompleteSearchOption
    props: HTMLAttributes<HTMLLIElement>
  }
>(({ option, props, ...rest }, ref) => {
  const { label, tags } = option

  return (
    <li {...props} {...rest} ref={ref}>
      <Grid container>
        <Grid item xs={6}>
          <Typography>{label}</Typography>
        </Grid>
        <Grid item display="flex" justifyContent="flex-end" xs={6}>
          <KubernetesListItemTags tags={tags} />
        </Grid>
      </Grid>
    </li>
  )
})

KubernetesAutocompleteSearchOption.displayName =
  'KubernetesAutocompleteSearchOption'

/**
 * This component is provided as an override to the default Autocomplete rendering
 * so that the autocomplete dropdown list is more visually separated from the
 * accordioned list below it
 */
const ElevatedPaper = (props) => {
  return <Paper elevation={8} {...props} />
}

// using the value from the MUI example
const LISTBOX_PADDING = 8

/**
 * Render each record in the autocomplete dropdown
 * They can be either a group name or a selectable search option
 * This passes down important styles from react-window that make the virtualization work
 */
const VirtualizedAutocompleteRow = React.forwardRef<HTMLLIElement>(
  (props: ListChildComponentProps, ref) => {
    const { data, index, style } = props
    const rowItem = data[index]
    const inlineStyle = {
      ...style,
      top: Number(style.top) + LISTBOX_PADDING
    }

    if (rowItem.hasOwnProperty('group')) {
      return (
        <TagTargetingAutocompleteGroupItem
          key={rowItem.group}
          ref={ref}
          css={inlineStyle}
          params={rowItem}
          type={ProvenanceTargetType.Kubernetes}
        />
      )
    }

    return (
      <KubernetesAutocompleteSearchOption
        key={`${rowItem[1].label}-${rowItem[1].uid}`}
        ref={ref}
        css={{
          ...inlineStyle,
          paddingTop: 6,
          paddingLeft: theme.space[5],
          paddingRight: theme.space[2],
          // we seem to have to force this here
          // perhaps the virtualization affects the default hover styling
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: theme.colors.gray[0]
          }
        }}
        option={rowItem[1]}
        props={rowItem[0]}
      />
    )
  }
)

VirtualizedAutocompleteRow.displayName = 'VirtualizedAutocompleteRow'

// this is from the MUI example
// it seems to be used to pass dynamic values down into the outer component of the virtualized list
const OuterElementContext = React.createContext({})
const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

// this is also part of the MUI virtualization code
function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null)
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

// Adapter for react-window
// taken from MUI virtualization example
const VirtualizedListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(
  props: { children: React.ReactChild[]; [key: string | number]: any },
  ref
) {
  const { children, ...otherProps } = props
  const itemData: React.ReactChild[] = []

  children.forEach(
    (item: React.ReactChild & { children?: React.ReactChild[] }) => {
      itemData.push(item)
      itemData.push(...(item.children || []))
    }
  )

  const itemCount = itemData.length
  // how many items we want react-window to render
  const numItemsInWindow = 8
  // how many items above and below the window we want react-window to pre-render so it updates quickly when scrolling
  const overscanCount = numItemsInWindow * 2

  // assume initial size of list items and group labels
  // but also measure them to ensure we have accurate values
  const assumedItemSize = 36
  const { height: itemSize, measurementRef: itemRef } =
    useHeightMeasurement<HTMLDivElement>(assumedItemSize)
  const assumedGroupLabelSize = 48
  const { height: groupLabelSize, measurementRef: groupLabelRef } =
    useHeightMeasurement<HTMLDivElement>(assumedGroupLabelSize)

  const getChildSize = (child: React.ReactChild) => {
    if (child.hasOwnProperty('group')) {
      return groupLabelSize
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > numItemsInWindow) {
      return numItemsInWindow * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  const listHeight = getHeight() + 2 * LISTBOX_PADDING

  // get the group label ref or item ref for the first of each type of item in the list
  const getRefProps = (props) => {
    const refProps: {
      ref?: React.Ref<HTMLElement>
    } = {}
    const firstGroupLabelIndex = props.data.findIndex((i) =>
      i.hasOwnProperty('group')
    )
    const firstItemIndex = props.data.findIndex(
      (i) => !i.hasOwnProperty('group')
    )

    if (props.index === firstGroupLabelIndex) {
      refProps.ref = groupLabelRef
    } else if (props.index === firstItemIndex) {
      refProps.ref = itemRef
    }

    return refProps
  }

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={otherProps}>
        <VariableSizeList
          ref={gridRef}
          height={listHeight}
          innerElementType="ul"
          itemCount={itemCount}
          itemData={itemData}
          itemSize={(index) => getChildSize(itemData[index])}
          outerElementType={OuterElementType}
          overscanCount={overscanCount}
          width="100%"
        >
          {(props) => {
            // attach ref for first group label and first item so we can measure them
            const refProps = getRefProps(props)

            return <VirtualizedAutocompleteRow {...props} {...refProps} />
          }}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

export const ServiceKubernetesTargetingSelection: React.FC<{
  clients: K8sClient[]
  setDisableFutureSteps(boolean): void
}> = ({ clients, setDisableFutureSteps }) => {
  const k8sClientsFlat = flattenKubernetesTargets(clients)
  const { labelKeys: targetOptions, availableTargetParams } =
    useFormattedK8sTargeting(k8sClientsFlat)

  const { getValues, resetField, setValue } =
    useFormContext<ServiceFormValues>()

  const selectedObjects = getValues('selectedObjects')

  // Display only the sections that are present in the K8s deployment
  const filteredSectionNames = KUBERNETES_TARGETING_SECTION_NAMES.filter(
    (sectionName) => Object.keys(targetOptions).includes(sectionName)
  )

  // List of options for the MUI Autocomplete component
  const optionList: K8sAutocompleteSearchOption[] = useMemo(() => {
    return Object.entries(targetOptions).reduce((options, [kind, targets]) => {
      const targetList = targets.map((targetInfo) => {
        const { children, name, target, uid } = targetInfo

        const unhealthyPods = []

        if (!isEmpty(children)) {
          Object.values(children)
            ?.flatMap((child) => child)
            .forEach((child) => {
              // @ts-ignore TODO: Create or find this type
              if (unhealthyPod(child) && child?.name) {
                // @ts-ignore TODO: Create or find this type
                unhealthyPods.push(child?.name)
              }
            })
        } else if (unhealthyPod(targetInfo) && targetInfo?.name) {
          unhealthyPods.push(targetInfo?.name)
        }

        return {
          section: kind,
          label: name,
          uid,
          tags: {
            cluster: target?.clusterId,
            namespace: target?.namespace,
            podCount: children?.POD?.length,
            replicaSetCount: children?.REPLICASET?.length,
            unhealthyPods
          },
          params: {
            kind,
            name,
            uid
          }
        }
      })

      return options.concat(targetList)
    }, [])
  }, [targetOptions])

  const handleSelectOption = useCallback(
    (_e, newValue) => {
      let copy

      // The MUI Autocomplete component with multiselect enabled returns an array of
      // selected options to the onChange function
      if (Array.isArray(newValue)) {
        copy = new Set<string>(newValue.map((value) => value.uid))
      } else {
        copy = new Set<string>(selectedObjects)

        if (copy.has(newValue.uid)) {
          copy.delete(newValue.uid)
        } else {
          copy.add(newValue.uid)
        }
      }

      // Set the selectedObjects form value before creating the new targetingStrategy
      setValue('selectedObjects', copy)

      const newTargetingStrategy = getKubernetesTargetingStrategy(
        k8sClientsFlat,
        getValues()
      )

      resetField('processId')
      resetField('processName')
      setValue('targetingStrategy', newTargetingStrategy)
      setValue('targetAll', copy.size === availableTargetParams.size)
      setDisableFutureSteps(true)
    },
    [
      availableTargetParams.size,
      getValues,
      k8sClientsFlat,
      resetField,
      selectedObjects,
      setDisableFutureSteps,
      setValue
    ]
  )

  const selectedOptionsList = useDeriveSelectedOptionsList(
    optionList,
    selectedObjects
  )

  return (
    <Stack data-test-kubernetes-targeting-selection gap={2}>
      <Autocomplete
        data-test-filter-tag-search
        disableListWrap
        multiple
        data-testid="kubernetes-tag-targeting-autocomplete"
        getOptionLabel={(option) => option.label}
        groupBy={(option) => option.section}
        isOptionEqualToValue={(option, value) => option.uid === value.uid}
        ListboxComponent={VirtualizedListboxComponent}
        options={optionList}
        PaperComponent={ElevatedPaper}
        popupIcon={null}
        renderGroup={(params) => params}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{
                      color: 'text.secondary',
                      marginLeft: 1,
                      marginRight: 1
                    }}
                  >
                    <Icon css={{ height: 14 }} icon="magnifyingGlass" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              )
            }}
            label="Targets"
            placeholder={
              Array.from(selectedObjects).length < 1
                ? 'Narrow the number of potential targets by name'
                : null
            }
          />
        )}
        renderOption={(props, option) => [props, option]}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => (
            <Chip
              key={`tag-${option.uid}`}
              {...getTagProps({ index })}
              color="primary"
              label={option.label}
              size="small"
              variant="filled"
            />
          ))
        }
        value={selectedOptionsList}
        onChange={handleSelectOption}
      />
      <section>
        {filteredSectionNames.map((sectionName) => {
          return (
            <KubernetesTagLabelSection
              key={sectionName}
              handleSelectOption={handleSelectOption}
              label={KUBERNETES_TARGETING_SECTION_DISPLAY_NAMES[sectionName]}
              options={optionList.filter(
                (option) => option.section === sectionName
              )}
              selectedOptions={selectedOptionsList}
            />
          )
        })}
      </section>
      <Stack alignItems="center" direction="row" display="flex" gap={1} pl={2}>
        <Typography color="text.secondary">
          <Icon icon="solidCircleInfo" />
        </Typography>
        <Typography color="text.secondary" variant="caption">
          Gremlin supports targeting parent Kubernetes objects. Pods will be
          listed if they don't belong to a Set or Deployment
        </Typography>
      </Stack>
    </Stack>
  )
}
